import React from "react";
import {
    Box, CircularProgress, IconButton, MenuItem,
    Select, Switch, Tooltip, Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {useTranslation} from "react-i18next";

export const REFRESH_INTERVALS = [
    { label: "5 s",  value: 5000  },
    { label: "10 s", value: 10000 },
    { label: "30 s", value: 30000 },
];

/**
 * Contrôles de rafraîchissement : bouton manuel + toggle auto + sélecteur d'intervalle.
 *
 * Props :
 *  - onRefresh           {Function}  callback de rafraîchissement manuel
 *  - refreshing          {boolean}   affiche le spinner sur le bouton
 *  - autoRefresh         {boolean}   état du toggle
 *  - onAutoRefreshChange {Function}  (value: boolean) => void
 *  - intervalMs          {number}    intervalle sélectionné en ms
 *  - onIntervalChange    {Function}  (value: number) => void
 */
export default function RefreshControls({
    onRefresh,
    refreshing = false,
    autoRefresh,
    onAutoRefreshChange,
    intervalMs,
    onIntervalChange,
}) {
    const {t} = useTranslation();
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>

            {/* Bouton refresh manuel */}
            <Tooltip title={t("components.refreshControls.refresh")}>
                <span>
                    <IconButton size="small" onClick={onRefresh} disabled={refreshing} color="primary">
                        {refreshing
                            ? <CircularProgress size={18} color="inherit"/>
                            : <RefreshIcon/>
                        }
                    </IconButton>
                </span>
            </Tooltip>

            {/* Toggle auto-refresh */}
            <Tooltip title={autoRefresh
                ? t("components.refreshControls.disableAutoRefresh")
                : t("components.refreshControls.enableAutoRefresh")
            }>
                <Box
                    sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer" }}
                    onClick={() => onAutoRefreshChange(!autoRefresh)}
                >
                    <Switch
                        size="small"
                        checked={autoRefresh}
                        onChange={(e) => onAutoRefreshChange(e.target.checked)}
                        color="primary"
                        onClick={(e) => e.stopPropagation()}
                    />
                    <Typography
                        variant="caption"
                        color={autoRefresh ? "primary.main" : "text.secondary"}
                        sx={{ fontWeight: autoRefresh ? 600 : 400, userSelect: "none" }}
                    >
                        {t("components.refreshControls.auto")}
                    </Typography>
                </Box>
            </Tooltip>

            {/* Sélecteur d'intervalle — visible seulement quand auto activé */}
            {autoRefresh && (
                <Select
                    size="small"
                    value={intervalMs}
                    onChange={(e) => onIntervalChange(Number(e.target.value))}
                    sx={{
                        fontSize: 12, height: 26, ml: 0.25,
                        "& .MuiSelect-select": { py: 0.25, px: 1 },
                    }}
                >
                    {REFRESH_INTERVALS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 12 }}>
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            )}
        </Box>
    );
}

